import { GridCallbackDetails } from '@mui/x-data-grid/models/api/gridCallbackDetails'
import type { GridRowParams } from '@mui/x-data-grid/models/params'
import {
  GridRowEditStartParams,
  GridRowEditStopParams,
} from '@mui/x-data-grid/models/params/gridRowParams'
import { MuiBaseEvent } from '@mui/x-data-grid-premium'
import {
  DataGridPro,
  DataGridProProps,
  GridCellParams,
  GridColDef,
  GridColumnMenuHideItem,
  GridColumnMenuManageItem,
  GridColumnMenuProps,
  GridColumnMenuSortItem,
  GridColumnVisibilityModel,
  GridRowHeightReturnValue,
  GridValidRowModel,
  MuiEvent,
  useGridApiRef,
} from '@mui/x-data-grid-pro'
import { frFR } from '@mui/x-data-grid-pro/locales'
import { styleConstants } from '@paywithextend/components'
import {
  base_colorContentBorder,
  blue100,
  blue150,
  grey200,
} from 'ext-ui/style/colors'
import { spacing } from 'ext-ui/style/spacing'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { renderCell } from 'src/components/DataGrid/cells/DataGridCell/DataGridCell'
import { renderHeader } from 'src/components/DataGrid/cells/DataGridHeaderCell/DataGridHeaderCell'
import useColumnsState from 'src/components/DataGrid/hooks/useColumnsState.hook'
import useTransferVerticalScroll from 'src/components/DataGrid/hooks/useTransferVerticalScroll.hook'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import { ExtendLocales } from 'src/enums'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectBrandColorFontPrimary } from 'src/redux/selectors/session.selectors'
import { InfiniteScrollProps } from 'src/types'
import { css } from 'styled-components'

export enum DataGridHeaderStyle {
  Default,
  NoBackground,
}

export const DataGridRowHeight = {
  Default: 52,
  Comfy: 64,
}

interface DataGridProps<T, R extends GridValidRowModel>
  extends Omit<
    DataGridProProps<R>,
    'localeText' | 'loading' | 'sortingMode' | 'onRowsScrollEnd'
  > {
  rowBuffer?: number
  columnBuffer?: number
  disableColumnVirtualization?: boolean
  height?: number
  rowHeight?: number
  infiniteScrollProps?: InfiniteScrollProps<T>
  filtersBody: T
  /**
   * Always includes `__check__`, `chevron`, and the first column by default.
   */
  alwaysVisibleColumns?: string[]
  headerStyle?: DataGridHeaderStyle
  /**
   * Follows the same format as `initialState` in `DataGridProProps`
   * However, `__check__` and the first column are always pinned left, and `chevron` is always pinned right
   * so you do not need to include them in the `pinnedColumns` property.
   */
  initialState?: DataGridProProps<R>['initialState']
  /**
   * When false, suppresses automatically pinning the first column on the left.
   */
  pinFirstColumn?: boolean
  /**
   * Optional ordered column fields override.
   */
  orderedColumnFields?: string[]
  /**
   * Registers a callback that resets column order back to the default order.
   */
  onRegisterResetColumnOrder?: (reset: () => void) => void
  /** Called when the user changes column order via drag/drop. */
  onUserColumnOrderChange?: () => void
  // any callbacks that use types from `GridEventListener` have no knowledge of the row model type
  // so we are manually defining them here with the generic we forward alone
  onRowEditStart?: (
    params: GridRowEditStartParams<R>,
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    details: GridCallbackDetails,
  ) => void
  onRowEditStop?: (
    params: GridRowEditStopParams<R>,
    event: MuiBaseEvent,
    details: GridCallbackDetails,
  ) => void
  onRowClick?: (
    params: GridRowParams<R>,
    event: React.MouseEvent<HTMLElement>,
    details: GridCallbackDetails,
  ) => void
  // custom event implementations from https://mui.com/x/react-data-grid/events/#catalog-of-events that don't have prop event handlers
  // i.e. onHeaderSelectionCheckboxChange?: GridEventListener<'headerSelectionCheckboxChange'>
}

export const handleCellClick = (
  _: GridCellParams,
  event: MuiEvent<React.MouseEvent>,
): void => {
  const target = event.target as HTMLElement
  if (event.metaKey || event.ctrlKey || target.closest('a')) {
    event.stopPropagation()
    return
  }
}

const DataGrid = <T, R extends GridValidRowModel>(
  props: DataGridProps<T, R>,
): React.ReactElement => {
  const brandColor = useSelector(selectBrandColorFontPrimary)
  const locale = useSelector(selectNullSafeLocaleFromState)

  const handleOnRowsScrollEnd = useCallback<
    NonNullable<DataGridProProps['onRowsScrollEnd']>
  >((): void => {
    if (
      !props?.infiniteScrollProps?.loading &&
      !props?.infiniteScrollProps?.complete
    ) {
      const page =
        props?.infiniteScrollProps?.page !== undefined
          ? props?.infiniteScrollProps?.page + 1
          : undefined
      const filtersBody =
        props.filtersBody !== undefined ? props.filtersBody : undefined
      props?.infiniteScrollProps?.fetch(filtersBody, page)
    }
  }, [
    props.filtersBody,
    props?.infiniteScrollProps?.fetch,
    props?.infiniteScrollProps?.loading,
    props?.infiniteScrollProps?.complete,
    props?.infiniteScrollProps?.page,
  ])

  const headerBgColor = useMemo(() => {
    switch (props.headerStyle) {
      case DataGridHeaderStyle.NoBackground:
        return styleConstants.colors.white
      case DataGridHeaderStyle.Default:
      default:
        return styleConstants.colors.gray_lightest
    }
  }, [props.headerStyle])

  const headerTopBorderColor = useMemo(() => {
    switch (props.headerStyle) {
      case DataGridHeaderStyle.NoBackground:
        return 'transparent'
      case DataGridHeaderStyle.Default:
      default:
        return base_colorContentBorder
    }
  }, [props.headerStyle])

  const loadingImage = (): React.ReactElement => <LoadingBackgroundPage />

  const customColumnMenu = (props: GridColumnMenuProps): React.ReactElement => {
    const itemProps = {
      colDef: props.colDef,
      onClick: props.hideMenu,
    }

    return (
      <div>
        <GridColumnMenuSortItem
          sx={{
            fontSize: 14,
            lineHeight: 18,
            color: brandColor,
            fontFamily: 'unset',
          }}
          {...itemProps}
        />
        <GridColumnMenuHideItem
          {...itemProps}
          sx={{
            fontSize: 14,
            lineHeight: 18,
            color: brandColor,
            fontFamily: styleConstants.fontFamilies.regular,
          }}
        />
        <GridColumnMenuManageItem
          sx={{
            fontSize: 14,
            lineHeight: 18,
            color: brandColor,
            fontFamily: styleConstants.fontFamilies.regular,
          }}
          {...itemProps}
        />
      </div>
    )
  }

  const {
    columns,
    getRowHeight,
    slots,
    slotProps,
    sx,
    columnVisibilityModel,
    apiRef: propApiRef,
    initialState,
    disableRowSelectionOnClick = true,
    editMode = 'row',
    pinFirstColumn = true,
    orderedColumnFields,
    onRegisterResetColumnOrder,
    onUserColumnOrderChange,
    ...rest
  } = props
  const localApiRef = useGridApiRef()
  const apiRef = useMemo(
    () => propApiRef ?? localApiRef,
    [propApiRef, localApiRef],
  )

  const defaultVisibleColumns = useMemo(
    () => {
      const visibleColumns = [
        '__check__',
        'chevron',
        'hover-actions',
        ...(pinFirstColumn && columns[0]?.field ? [columns[0]?.field] : []),
        ...(props.alwaysVisibleColumns ?? []),
      ]
      return visibleColumns
    },
    [props.alwaysVisibleColumns, columns, pinFirstColumn],
  )

  const getTogglableColumns = (columns: GridColDef[]): string[] => {
    return columns
      .filter(
        (column) =>
          !defaultVisibleColumns.includes(column.field) && column.hideable,
      )
      .map((column) => column.field)
  }

  const columnsTyped: Array<GridColDef<R>> = useMemo(() => {
    const hideableFromAlwaysVisibleColumns = (
      field: string,
    ): boolean | undefined => {
      return !defaultVisibleColumns.includes(field)
    }

    return columns.map((column) => ({
      ...column,
      renderHeader: column.renderHeader ?? renderHeader,
      renderCell: column.renderCell ?? renderCell,
      hideable:
        column.hideable ?? hideableFromAlwaysVisibleColumns(column.field),
    }))
  }, [columns, defaultVisibleColumns, renderHeader, renderCell])

  const columnVisibilityModelOverride = useMemo(() => {
    const visibilityOverride =
      defaultVisibleColumns.reduce<GridColumnVisibilityModel>((acc, field) => {
        acc[field] = true
        return acc
      }, {})

    return { ...columnVisibilityModel, ...visibilityOverride }
  }, [columnVisibilityModel, defaultVisibleColumns])

  const columnsState = useColumnsState(apiRef, columnsTyped, orderedColumnFields)

  useEffect(() => {
    onRegisterResetColumnOrder?.(columnsState.resetColumnOrder)
  }, [onRegisterResetColumnOrder, columnsState.resetColumnOrder])

  const handleColumnOrderChange = useCallback<
    NonNullable<DataGridProProps['onColumnOrderChange']>
  >(
    () => {
      columnsState.onColumnOrderChange()
      onUserColumnOrderChange?.()
    },
    [columnsState.onColumnOrderChange, onUserColumnOrderChange],
  )

  const rootRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const root = rootRef.current
    if (!root) return

    const main = root.querySelector<HTMLElement>('.MuiDataGrid-main')
    const scroller = root.querySelector<HTMLElement>(
      '.MuiDataGrid-virtualScroller',
    )
    if (!main || !scroller) return

    const compute = () => {
      const style = getComputedStyle(main)

      const leftPinned =
        parseFloat(style.getPropertyValue('--DataGrid-leftPinnedWidth')) || 0
      const rightPinned =
        parseFloat(style.getPropertyValue('--DataGrid-rightPinnedWidth')) || 0

      // Raw scrollable range the browser exposes
      const fullMax = Math.max(
        0,
        Math.round(scroller.scrollWidth - scroller.clientWidth),
      )

      // Effective scrollable range of the CONTENT (excluding pinned on both sides)
      const maxEffective = Math.max(
        0,
        Math.round(fullMax - leftPinned - rightPinned),
      )

      // guard against sub-pixel/retina fuzz
      const EPS = (window.devicePixelRatio || 1) > 1 ? 2 : 1

      // If there is no effective overflow, hide both fades
      if (maxEffective <= EPS) {
        main.setAttribute('data-left-fade', '0')
        main.setAttribute('data-right-fade', '0')
        return
      }

      const sl = Math.round(scroller.scrollLeft)

      // At start when we're within EPS of 0
      const atStart = sl <= EPS

      // At end when the remaining EFFECTIVE scroll (excluding right pinned) is within EPS
      // i.e., we've scrolled to fullMax - rightPinned
      const atEnd = fullMax - rightPinned - sl <= EPS

      main.setAttribute('data-left-fade', atStart ? '0' : '1')
      main.setAttribute('data-right-fade', atEnd ? '0' : '1')
    }

    let ticking = false
    const onScroll = () => {
      if (ticking) return
      ticking = true
      requestAnimationFrame(() => {
        compute()
        ticking = false
      })
    }

    // run after layout settles
    requestAnimationFrame(() => {
      compute()
      requestAnimationFrame(compute)
    })

    scroller.addEventListener('scroll', onScroll, { passive: true })

    // update when width/columns change
    const ro = new ResizeObserver(() => compute())
    ro.observe(scroller)
    const content = root.querySelector('.MuiDataGrid-virtualScrollerContent')
    if (content) ro.observe(content as Element)

    // update on window resize too
    const onWinResize = () => requestAnimationFrame(compute)
    window.addEventListener('resize', onWinResize)

    return () => {
      scroller.removeEventListener('scroll', onScroll)
      ro.disconnect()
      window.removeEventListener('resize', onWinResize)
    }
  }, [])
  useTransferVerticalScroll(rootRef, '.MuiDataGrid-virtualScroller')

  return (
    <div
      ref={rootRef}
      css={css`
        width: 100%;
        height: ${(props.height || 0) - 60}px;
      `}
    >
      <DataGridPro
        rowBuffer={props.rowBuffer ?? 20}
        columnBuffer={props.columnBuffer ?? 4}
        apiRef={apiRef}
        // overridden props, should be Omitted from extended type
        localeText={
          locale === ExtendLocales.frCA
            ? frFR?.components.MuiDataGrid.defaultProps.localeText
            : undefined
        }
        loading={props?.infiniteScrollProps?.loading}
        onRowsScrollEnd={handleOnRowsScrollEnd}
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        editMode={editMode}
        sortingMode={'server'}
        // forwarded props with custom behaviour
        columns={columnsState.columns}
        onColumnWidthChange={columnsState.onColumnWidthChange}
        onColumnOrderChange={handleColumnOrderChange}
        getRowHeight={
          (getRowHeight ?? props.rowHeight)
            ? undefined
            : (): GridRowHeightReturnValue => 'auto'
        }
        columnHeaderHeight={44}
        slots={{
          loadingOverlay: loadingImage,
          columnMenu: customColumnMenu,
          ...slots,
        }}
        slotProps={{
          columnsManagement: {
            getTogglableColumns,
            disableShowHideToggle: true,
          },
          ...slotProps,
        }}
        sx={{
          '--DataGrid-rowBorderColor': base_colorContentBorder,
          borderRadius: 0,
          borderStyle: 'unset',
          borderTop: `1px solid ${headerTopBorderColor}`,
          // disable cell selection style
          '.MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          // disable cell selection style when editing or clicked
          '.MuiDataGrid-cell.MuiDataGrid-cell--editing:focus-within': {
            outline: 'none',
          },
          '.MuiDataGrid-cell.MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          // row styling
          // selected
          '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: blue100,
          },
          '& .MuiDataGrid-virtualScroller': {
            willChange: 'transform',
            transform: 'translateZ(0)',
            contain: 'layout paint',
            backfaceVisibility: 'hidden',
            overscrollBehaviorY: 'contain',
            overscrollBehaviorX: 'none',
          },
          '& .MuiDataGrid-virtualScrollerContent .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedRight':
            {
              backgroundColor: blue100,
            },
          '& .MuiDataGrid-virtualScrollerContent .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell--pinnedLeft':
            {
              backgroundColor: blue100,
            },
          '--hover-color': `${grey200}`,
          '& .MuiDataGrid-row:hover': {
            backgroundColor: 'var(--hover-color)',
            cursor: props.onRowClick ? 'pointer' : 'default',
          },
          // selected & hovered
          '& .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell': {
            backgroundColor: blue150,
          },
          '& .MuiDataGrid-virtualScrollerContent .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell--pinnedLeft':
            {
              backgroundColor: blue150,
            },
          '& .MuiDataGrid-virtualScrollerContent .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell--pinnedRight':
            {
              backgroundColor: blue150,
            },
          // editing row
          '.MuiDataGrid-row--editing': {
            boxShadow: 'none',
          },
          // defining a class to hide right border on column header
          '& .hideRightSeparator > .MuiDataGrid-columnSeparator': {
            display: 'none',
          },
          '& .MuiDataGrid-columnHeaderCheckbox > .MuiDataGrid-columnSeparator':
            {
              display: 'none',
            },
          // disable cell overflow to support tooltips
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            padding: 0,
          },
          '.MuiDataGrid-cell.MuiDataGrid-cell--editing': {
            display: 'flex',
            alignItems: 'center',
            boxShadow: 'none',
            padding: 0,
          },
          // column header bg color
          '.MuiDataGrid-columnHeader': {
            padding: '0 16px',
            backgroundColor: headerBgColor,
            borderLeftStyle: 'unset',
          },
          // remove cursor pointer and blue outline on column headers
          '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-cell:focus': {
            outline: 'none',
            cursor: 'unset',
          },
          // keep column resize indicator even for pinned columns
          '.MuiDataGrid-columnHeader--withRightBorder': {
            borderRightStyle: 'unset',
            '& .MuiDataGrid-columnSeparator': {
              opacity: 1,
              transform: 'translateX(-1px)',
            },
          },
          '.MuiDataGrid-columnHeader--withLeftBorder': {
            borderLeftStyle: 'unset',
            '& .MuiDataGrid-columnSeparator': {
              opacity: 1,
              transform: 'translateX(1px)',
            },
          },
          '.MuiDataGrid-columnHeader--pinnedLeft': {
            zIndex: 31,
          },
          // style resets for columns
          '.MuiDataGrid-columnHeaderTitleContainer': {
            width: '100%',
          },
          '.MuiDataGrid-columnHeaderTitleContainerContent': {
            width: '100%',
          },
          '.MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainerContent':
            {
              justifyContent: 'center',
            },
          '.MuiDataGrid-columnHeader.MuiDataGrid-columnHeaderCheckbox': {
            padding: 0,
          },

          // prevent scrollbar from showing over flyouts
          '& .MuiDataGrid-scrollbar': {
            zIndex: 1,
          },
          // show action buttons on hover (add 'hover-actions' to the affected column's cellClassName property)
          '.MuiDataGrid-row': {
            '.hover-actions': {
              maxWidth: 0,
              '& > *': {
                opacity: 0,
                pointerEvents: 'none',
              },
              backgroundColor: 'transparent',
              borderLeft: 'none',
              borderRight: `1px solid ${base_colorContentBorder}`,
              overflow: 'visible',

              right: '0px',
              justifyContent: 'right',
            },
            '&:hover > .hover-actions > *': {
              pointerEvents: 'all',
              opacity: 1,
            },
          },
          // hide column header
          '.hover-actions-header': {
            pointerEvents: 'none',
            opacity: 0,
            maxWidth: 0,
            padding: 0,
          },
          // column header row
          '.MuiDataGrid-columnHeaders .MuiDataGrid-filler': {
            backgroundColor: headerBgColor,
            '--DataGrid-t-header-background-base': headerBgColor,
          },
          // overrides for hover menu to rightAlign header text properly
          '.MuiDataGrid-columnHeader .MuiDataGrid-menuIcon': {
            marginRight: 0,
          },
          '.MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIcon': {
            marginRight: `-${spacing(2)}`,
          },
          position: 'relative',
          // make the main container a positioning context
          '& .MuiDataGrid-main': { position: 'relative', zIndex: 0 },

          // LEFT fade (hidden by default)
          '& .MuiDataGrid-main::before': {
            content: '""',
            position: 'absolute',
            top: 'var(--DataGrid-headerHeight, 44px)',
            bottom: 0,
            left: 'var(--DataGrid-leftPinnedWidth, 0px)',
            width: '10px',
            pointerEvents: 'none',
            zIndex: 2,
            background: `
              linear-gradient(
                to right,
                rgba(0,0,0,0.10) 0px,
                rgba(0,0,0,0.06) 3px,
                rgba(0,0,0,0.03) 6px,
                rgba(0,0,0,0.015) 8px,
                rgba(0,0,0,0) 10px
              ) 0 0 / 16px 100% no-repeat
            `,
            opacity: 0,
            transition: 'opacity 150ms ease-out',
          },

          // RIGHT fade (hidden by default)
          '& .MuiDataGrid-main::after': {
            content: '""',
            position: 'absolute',
            top: 'var(--DataGrid-headerHeight, 44px)',
            bottom: 0,
            right: 'var(--DataGrid-rightPinnedWidth, 0px)',
            width: '10px',
            pointerEvents: 'none',
            zIndex: 2,
            background: `
              linear-gradient(
                to left,
                rgba(0,0,0,0.10) 0px,
                rgba(0,0,0,0.06) 3px,
                rgba(0,0,0,0.03) 6px,
                rgba(0,0,0,0.015) 8px,
                rgba(0,0,0,0) 10px
              ) 100% 0 / 16px 100% no-repeat
            `,
            opacity: 0,
            transition: 'opacity 150ms ease-out',
          },

          // toggle visibility based on scroll position
          '& .MuiDataGrid-main[data-left-fade="1"]::before': { opacity: 1 },
          '& .MuiDataGrid-main[data-right-fade="1"]::after': { opacity: 1 },
          ...sx,
        }}
        columnVisibilityModel={columnVisibilityModelOverride}
        initialState={{
          pinnedColumns: {
            right: [
              'hover-actions',
              ...(initialState?.pinnedColumns?.right ?? []),
              'chevron',
            ],
            left: [
              '__check__',
              ...(pinFirstColumn && columns[0]?.field ? [columns[0]?.field] : []),
              ...(initialState?.pinnedColumns?.left ?? []),
            ],
          },
        }}
        rowSelectionPropagation={{ parents: false, descendants: false }}
        // props with default behaviour
        {...rest}
      />
    </div>
  )
}
export default DataGrid
